import {
  Bold,
  BoldValues,
  findClosestBoldValueFont,
  getBoldValuesFromCustomFonts,
  getCustomFontFromPostScriptName,
} from '@src/utility/fonts';

import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { BrandingCustomFont } from '@src/types.ts/story';

export interface CaptionFontVariant {
  weight: number;
  style?: 'normal' | 'italic';
}

/**
 * The caption config patch for a font pick from the captions font picker
 * (`FontFamilyVariantDropdown`). Shared by the Captions panel and the
 * captions bar (PLAT-12100 DS2), so a font picked in either lands the same.
 *
 * - An explicit brand variant applies its own weight and style verbatim: the
 *   closest-weight resolution below would override the user's pick and drop
 *   an italic face.
 * - A brand font picked by family snaps to its weight closest to regular.
 * - A popular font resets to regular weight.
 * - No font keeps the current weight.
 */
export function resolveCaptionFont(
  fontScriptName: string | undefined,
  variant: CaptionFontVariant | undefined,
  currentWeight: KaraokeConfig['font_weight'],
  brandCustomFonts: BrandingCustomFont[],
): Partial<KaraokeConfig> {
  if (fontScriptName && variant) {
    return {
      font_family: fontScriptName,
      font_weight: variant.weight.toString(),
      // Popular-font weight picks carry no style — the italic toggle owns
      // font_style there; brand variants specify their face's own.
      ...(variant.style ? { font_style: variant.style } : {}),
    };
  }
  let fontWeight = currentWeight;
  let fontFamily = fontScriptName;
  if (fontScriptName) {
    const isCustomFont = !!getCustomFontFromPostScriptName(
      fontScriptName,
      brandCustomFonts,
    );
    if (isCustomFont) {
      const { fontsByWeight } = getBoldValuesFromCustomFonts(
        fontScriptName,
        brandCustomFonts,
      );
      const closestBold = findClosestBoldValueFont(fontsByWeight);
      if (closestBold) {
        fontWeight = closestBold.weight.toString();
        fontFamily = closestBold.font.postScriptName;
      }
    } else {
      fontWeight = BoldValues[Bold.normal].toString();
    }
  }
  return { font_family: fontFamily, font_weight: fontWeight };
}
